// Feature flags driven by Vite env vars (prefix `VITE_FEATURE_*`).
//
// Each flag has a default, ON for all but `seniorPreorder`. Only the literal
// opposite of the default (`false` for an ON flag, `true` for an OFF one,
// case-insensitive) moves it. Any other value — missing, empty or a typo —
// keeps the default, so a flag has to be flipped explicitly and a mistake in
// an env file never flips one by accident.
//
// Flags are evaluated at module load. They don't change at runtime — to
// flip a flag, edit the env file and restart the dev server (or rebuild).
// That's intentional: feature flags here are deploy-time toggles, not
// runtime experiments.

export function parseFlag(value: string | undefined, defaultValue: boolean): boolean {
  const opposite = defaultValue ? 'false' : 'true'
  return value?.trim().toLowerCase() === opposite ? !defaultValue : defaultValue
}

export const featureFlags = {
  /** Senior Yearbook photo selection (Deviation2). Gates `mainStore.yearbookContext`. */
  yearbookSelection: parseFlag(import.meta.env.VITE_FEATURE_YEARBOOK_SELECTION, true),
  /** Senior Portfolio Tracker (Deviation3). Gates `mainStore.portfolioTrackerDetail`. */
  portfolioTracker: parseFlag(import.meta.env.VITE_FEATURE_PORTFOLIO_TRACKER, true),
  /**
   * Package Builder modal (Deviation4). Gates both the PLP / upsell add-to-cart
   * interception via `bundleHasSheetSelectionGroup` and the downstream
   * `ItemGroupMode.SheetSelection` suppression in `CartConfigData`. When off,
   * SheetSelection bundles fall through to the existing add-to-cart path
   * (which today rejects server-side without populated `selectedItems[]`) and
   * the SheetSelection group re-surfaces in the Select Poses cart-customization
   * UI. Intended as a deploy-time kill switch — degraded-off is acceptable.
   */
  packageBuilder: parseFlag(import.meta.env.VITE_FEATURE_PACKAGE_BUILDER, true),
  /**
   * Terms & Conditions acceptance gate. Blocks the gallery behind a blocking
   * acceptance sheet on every genuine session entry, and forwards unaccepted
   * sessions to the market landing via a router guard. Off in local dev
   * (`.env`), on in demo and production. Defaults ON so a missing env var fails
   * safe — the compliance gate shows rather than silently disappearing.
   */
  termsAcceptanceGate: parseFlag(import.meta.env.VITE_FEATURE_TERMS_ACCEPTANCE_GATE, true),
  /**
   * Cart flyout enhancements (SUN-292). One switch over three behaviours that have to move together —
   * auto-open, the reward-unlock animation, and the suppression of add/reward toasts. Read only through
   * `useEnhancedFlyout`, never directly, so there is a single place the composition lives.
   *
   * Off means the flyout reverts to its pre-SUN-292 behaviour wholesale, legacy toasts included. That
   * is the point: toast suppression is *coupled* to the enhancement being active, so turning the
   * enhancement off without restoring the toasts would leave a cart that mutates with no feedback at
   * all.
   *
   * **It governs the storefront, not the sandbox.** "Put it back the way it was" is a request only a
   * page with a previous way can honour, and `/debug/cart-flyout-enhancement` has none — it renders
   * the new sequence and nothing else. So that route opts in as `CartFlyoutEnhancement.Always` and
   * this switch does not reach it. That mattered most through phase 1, when the switch was off
   * everywhere deployed and the sandbox was the only place the business could evaluate the sequence.
   * Phase 2 (A2b, A3) has since wired the sequence to real cart mutations and the switch is on, but
   * the separation still holds: turning it off must not take the sandbox down with it.
   */
  cartFlyoutEnhancement: parseFlag(import.meta.env.VITE_FEATURE_CART_FLYOUT_ENHANCEMENT, true),
  /**
   * Senior Preorder (Last Chance Prepay). Gates `resolveSeniorPreorderRoute` at
   * its single choke point: when off, every subject resolves `None` + `Dashboard`,
   * so a direct `/senior/preorder?s=TOKEN` forwards to the CADY dashboard.
   * Everything else that keys off the resolved context — the dropped full-page
   * Retouching step and "Order for {name}" relabel in `commerce.ts`, the hidden
   * checkout step counter — reverts with it. `/senior?s=TOKEN` is normal senior
   * ordering either way: the preorder page is reached only through explicit links.
   *
   * ⚠️ Defaults **OFF**, unlike every other flag here. The others gate features
   * already live, where a missing env var should keep them working; this one gates
   * an unlaunched feature. Fail-closed is the safe direction until the business launches it —
   * a forgotten env var leaves senior ordering exactly as it is today.
   *
   * The `?preview` dry-run deliberately bypasses this flag (see
   * `buildSeniorPreorderPreview`) so support and QA can still see how a token
   * would route once the feature is enabled.
   */
  seniorPreorder: parseFlag(import.meta.env.VITE_FEATURE_SENIOR_PREORDER, false),
  /**
   * Selection upgrade on cart/configure (SUN-344, "paper-type upgrade" to the
   * business): silently moves every selectable group the page renders to its
   * highest-priced option on each load until the customer confirms with
   * Continue (`applyMaxPriceSelectionUpgrade`). Off leaves the add-time default
   * in place.
   *
   * ⚠️ Off is only safe because the phase-1 catalog reorder (2026-09-10) made
   * the $0 option the add-time default — the add path picks the lowest `zOrder`,
   * not the cheapest. If that ordering is reverted, off restores the price jump
   * at add-to-cart and this stops being a usable kill switch.
   */
  selectionUpgradeOnCartConfigure: parseFlag(
    import.meta.env.VITE_FEATURE_SELECTION_UPGRADE_ON_CART_CONFIGURE,
    true
  )
}
